import { App, Plugin, watch, defineAsyncComponent, type Component, markRaw } from 'vue';
import { useAppOptionsStore } from './stores/app-options';
import JsonViewer from 'vue3-json-viewer';
import 'vue3-json-viewer/dist/vue3-json-viewer.css';
import * as Sentry from '@sentry/vue';

import type { User } from '@lib/types';
import { configureExtensions, setupExtensions, type ExtensionOptions } from '@lib/extensions';

export interface EfLibOptions {
	search?: {
		callback?: (s: string) => boolean;
		component?: Component;
		displayBreakpoint?: string;
	};
	//
	plugins?: Record<string, unknown>;
	//
	layout?: {
		context_header_slot?: Component;
	};
	/** The app's own tabs and items in the slots of the lib's pages, see lib/docs/writing-an-extension.md. */
	contributions?: ExtensionOptions['contributions'];
	/** Which contributions a slot leaves out or puts first. */
	slots?: ExtensionOptions['slots'];
	/**
	 * The palette every colour field (efColorInput) offers beside its picker, as `#rrggbb`, in
	 * place of the lib's: the primary, Bootstrap's colours and more, each with a light version.
	 */
	colorPresets?: string[];
}

const plugin: Plugin = {
	install: (app: App, options?: EfLibOptions) => {
		const auth = app.config.globalProperties.$auth;
		const appOptions = useAppOptionsStore();
		if (options?.search) {
			appOptions.search = {
				callback: options.search.callback,
				component: options.search.component ? markRaw(options.search.component) : undefined,
				displayBreakpoint: options.search.displayBreakpoint,
			};
		}
		//
		const context_header_slot = options?.layout?.context_header_slot ?? null;
		if (context_header_slot) {
			// If slot is a function returning a Promise (lazy import)
			if (typeof context_header_slot === 'function' && context_header_slot.prototype === undefined) {
				appOptions.contextHeaderSlot = markRaw(
					defineAsyncComponent(context_header_slot as () => Promise<Component>),
				);
			} else {
				// Normal component
				appOptions.contextHeaderSlot = markRaw(context_header_slot as Component);
			}
		} else {
			appOptions.contextHeaderSlot = null;
		}

		appOptions.colorPresets = options?.colorPresets ?? null;

		// The app's contributions and slot options, then the setup of the extensions that are on
		configureExtensions({ contributions: options?.contributions, slots: options?.slots });
		setupExtensions(app);

		// Use some plugins we need later
		app.use(JsonViewer);

		// Add sentry.io
		if (!!window.env?.sentry_dsn) {
			Sentry.init({
				app,
				dsn: window.env.sentry_dsn,
				// Setting this option to true will send default PII data to Sentry.
				// For example, automatic IP address collection on events
				sendDefaultPii: true,
			});
		}

		// Action on Login/Logout and user change
		watch(
			() => auth.user(),
			async (user: User) => {
				// User logged in
				if (user) {
					// Setup Sentry user information
					Sentry.setUser({ id: user.id, email: user.email });
				}
				// User logged out
				else {
					// Remove Sentry user Info
					Sentry.setUser(null);
				}
			},
		);
	},
};

export default plugin;
